import React from 'react';
import {
  Box,
  Dropdown,
  HStack,
  Icongraphy,
  NewContentButton,
  Text,
  VStack,
} from '@design-system/pc';
import ShopDetailCustomerReviewCard from './ShopDetailCustomerReviewCard';
import useShopDetailCustomerReview from './hooks/useShopDetailCustomerReview';
import TrackingService from '@/services/tracking';

interface ShopDetailCustomerReviewProps {
  sldToCd: string;
  ssrData?: Parameters<typeof useShopDetailCustomerReview>[0]['ssrData'];
}

const ShopDetailCustomerReview = ({
  sldToCd,
  ssrData,
}: ShopDetailCustomerReviewProps) => {
  const {
    statistics,
    reviews,
    isLastPage,
    totalElements,
    sortType,
    handleClickMore,
    handleChangeSort,
    gdsMeritTagsWithEmoji,
    hasRating,
    hasMeritTags,
  } = useShopDetailCustomerReview({ sldToCd, ssrData });

  if (!reviews || reviews.length === 0) return null;

  const renderColumn = ({
    isRightColumn,
    columnName,
  }: {
    isRightColumn: boolean;
    columnName: 'left' | 'right';
  }) => {
    const columnReviews = reviews.filter(
      (_, i) => i % 2 === (isRightColumn ? 1 : 0),
    );
    return (
      <VStack flex={1} gap={16} alignItems="stretch" data-column={columnName}>
        {columnReviews.map((review, idx) => {
          const gtmIndex = isRightColumn ? idx * 2 + 2 : idx * 2 + 1;
          return (
            <ShopDetailCustomerReviewCard
              key={`review-${review.intEvalMstId}-${columnName}-${idx}`}
              review={review}
              tagName={`shop_detail_review`}
              tagValues={{
                index: gtmIndex,
                review: review.intEvalMstId,
              }}
            />
          );
        })}
      </VStack>
    );
  };

  return (
    <Box py={80} borderTop="1px solid" borderTopColor="color_chip.gray.200">
      <Text variant="headline4" color="color_chip.primary" mb={16}>
        매장 고객 후기
      </Text>

      {(hasRating || hasMeritTags) && (
        <Box px={44} py={28}>
          <HStack width="100%" justifyContent="center">
            {hasRating && (
              <Box width="50%" display="flex" justifyContent="center">
                <HStack gap={52} width="340px" justifyContent="space-between">
                  <Box>
                    <HStack gap={4} justifyContent="center" mb="12px">
                      <Icongraphy
                        variant="ic_star_solid"
                        resize
                        size={24}
                        color="color_chip.primary"
                      />
                      <Text variant="etc01" color="color_chip.primary">
                        {statistics?.shopStatistics?.totAvgScore?.toFixed(1) ||
                          0}
                      </Text>
                    </HStack>
                    <Text variant="caption1" color="color_chip.gray.500">
                      (최근 6개월 후기 기준 산출)
                    </Text>
                  </Box>

                  <HStack gap={12}>
                    {Object.entries(
                      statistics?.shopStatistics?.scoreGroups || {},
                    )
                      .sort((a, b) => Number(b[0]) - Number(a[0]))
                      .map(([key, count], index) => (
                        <VStack key={`score-bar-${index}`} gap="12px">
                          <VStack
                            space="end"
                            width="6px"
                            height="50px"
                            backgroundColor="color_chip.gray.200"
                            borderRadius="1px"
                            overflow="hidden"
                          >
                            <Box
                              width="6px"
                              height={`${count}%`}
                              backgroundColor="color_chip.primary"
                            />
                          </VStack>
                          <Text variant="caption3" color="color_chip.gray.500">
                            {key}점
                          </Text>
                        </VStack>
                      ))}
                  </HStack>
                </HStack>
              </Box>
            )}
            {hasMeritTags && (
              <Box width="50%" display="flex" justifyContent="center">
                <VStack gap="8px" width="364px">
                  {gdsMeritTagsWithEmoji.map((tag) => (
                    <HStack
                      key={`gds-merit-tag-${tag.code}-${tag.name}`}
                      width="100%"
                    >
                      <Text variant="body2" color="color_chip.primary" pr={16}>
                        {tag.emoji} {tag.desc}
                      </Text>
                      <Box
                        flex={1}
                        borderBottom="1px dashed"
                        borderColor="color_chip.gray.400"
                      />
                      <Text variant="body2" color="color_chip.primary" pl={12}>
                        {tag.count}명
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )}
          </HStack>
        </Box>
      )}

      <Box>
        <HStack justifyContent="space-between" mb={16}>
          <HStack gap={4}>
            <Text variant="body1" color="color_chip.primary">
              전체
            </Text>
            <HStack>
              <Text variant="headline6" color="color_chip.primary">
                {totalElements}
              </Text>
              <Text variant="body1" color="color_chip.primary">
                건
              </Text>
            </HStack>
          </HStack>
          <Dropdown
            width="130px"
            size="md"
            selectValue={sortType}
            placeholder="추천순"
            option={{
              data: [
                { value: 'RECOMMENDED', text: '추천순', isDisabled: false },
                { value: 'LATEST', text: '최신순', isDisabled: false },
              ],
            }}
            variant="list"
            handleChange={handleChangeSort}
          />
        </HStack>

        <HStack gap={12} alignment="start">
          {renderColumn({ isRightColumn: false, columnName: 'left' })}
          {renderColumn({ isRightColumn: true, columnName: 'right' })}
        </HStack>

        {!isLastPage && (
          <Box textAlign="center" mt={20}>
            <NewContentButton
              emphasis="low"
              icon={{ align: 'right', variant: 'ic_arrow_down' }}
              size="lg"
              width="114px"
              onClick={handleClickMore}
              {...TrackingService.tagGTM(
                'interior_review_shop_detail_more_button',
              )}
            >
              더보기
            </NewContentButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ShopDetailCustomerReview;
